// ambient-provenance.js — LAYER 1 of the ambient-pipeline provenance system
// (owner order, 2026-08-13).
//
// WHAT THIS IS, IN PLAIN WORDS
// The ambient coder attaches a medical code to a diagnosis without asking the
// doctor and without showing anything (lib/ambient-dx-coding.js). This file
// builds the permanent answer to three questions about that code:
//
//   WHERE DID IT COME FROM?   the exact words, and whether the doctor typed them
//                             or spoke them (with the quote and its position in
//                             the saved transcript).
//   THROUGH WHAT?             which model transcribed, which model extracted,
//                             which terminology release answered, which map
//                             translated — every identity read from the server's
//                             own configuration, never written here by hand.
//   DECIDED HOW?              which rule fired, and which candidates it saw.
//
// The answer is a FHIR Provenance resource. It follows the SAME conventions as
// the two Provenance resources this app already writes (medplum-consult-extras.js
// sections 4 and 5): an `activity` coding, an `agent` for the person, and the
// structured payload in the shared opd-healther extension bag.
//
// NOTHING HERE IS SHOWN TO THE DOCTOR. This module renders nothing. The
// builders are pure; the one write (recordAmbientCodingProvenance, at the end of
// the file) goes out on the doctor's own token through the same row-level
// security as every other consult write.
//
// LAYER 2 IS THE SUPERSET. buildAmbientTraceStages below emits the SAME facts
// for the engineering journal (server/opd/pipeline-trace.mjs), plus the raw
// inputs and outputs Layer 1 must not carry. One traceId joins them.

import { getMedplum, apiUrl, authHeader } from './client-port.js';

/** The shared opd-healther extension bag — the same url every other lib uses. */
export const EXT_OPD_HEALTHER = 'http://healther.io/fhir/StructureDefinition/opd-healther';

/** Our own activity code system — the same one the flag-resolve Provenance uses. */
export const OPD_ACTIVITY_SYSTEM = 'http://healther.io/CodeSystem/opd-activity';
/** The activity this Provenance records. */
export const AMBIENT_ACTIVITY_CODE = 'ambient-code';

/** The version of the decision rules this record was produced by. */
export const AMBIENT_CODER_VERSION = 'ambient-dx-coding@1';

/**
 * A run id. One id per ambient coding attempt that produced a code. It is stored
 * on the Provenance and posted with every Layer-2 stage, so an auditor can read
 * one journal file and find the matching clinical record, or the other way
 * round. `rand` and `now` are injectable so a test gets a fixed id.
 * @param {string} encounterId
 * @param {{now?: number, rand?: () => number}} [seam]
 * @returns {string}
 */
export function newTraceId(encounterId, { now = Date.now(), rand = Math.random } = {}) {
  const tail = Math.floor(rand() * 0xffffff).toString(16).padStart(6, '0');
  return `amb-${String(encounterId || 'unknown').slice(0, 12)}-${now.toString(36)}-${tail}`;
}

/** Compare words the way `pickUnambiguousCoding` does — case and spacing only. */
function normForFind(s) {
  return String(s == null ? '' : s).toLowerCase().replace(/[\s\u00a0]+/g, ' ').trim();
}

/**
 * WHERE THE WORDS CAME FROM.
 *
 * Look for the diagnosis words in the saved verbatim transcript. When they are
 * there, this is a SPOKEN diagnosis and we record the exact quote, who said it,
 * which turn it was, and the character position inside that turn. When they are
 * not there, the doctor TYPED them, and we say so.
 *
 * WE NEVER GUESS. The match is a plain case-insensitive substring search on the
 * turn text. There is no fuzzy matching, no stemming and no nearest neighbour:
 * a span we are not sure about would be a false claim about the patient's words,
 * which §2 of AGENTS.md forbids. A miss simply reports `typed`.
 *
 * @param {string} text  the diagnosis words the coder used
 * @param {Array<{speaker?:string, text?:string, startMs?:number, endMs?:number}>} turns
 * @returns {{kind:'transcript'|'typed', quote?:string, speaker?:string, turnIndex?:number,
 *           charStart?:number, charEnd?:number, startMs?:number|null, endMs?:number|null}}
 */
export function findVerbatimSpan(text, turns) {
  const wanted = normForFind(text);
  if (!wanted) return { kind: 'typed' };
  const list = Array.isArray(turns) ? turns : [];
  for (let i = 0; i < list.length; i += 1) {
    const turn = list[i];
    const raw = typeof turn === 'string' ? turn : (turn && turn.text);
    const hay = normForFind(raw);
    if (!hay) continue;
    const at = hay.indexOf(wanted);
    if (at < 0) continue;
    return {
      kind: 'transcript',
      // The quote is taken from the NORMALIZED turn so the offsets below point
      // at exactly these characters. The untouched turn stays in the transcript
      // Media, which is the sacred copy — this is a pointer, not a replacement.
      quote: hay.slice(at, at + wanted.length),
      speaker: (turn && turn.speaker) || null,
      turnIndex: i,
      charStart: at,
      charEnd: at + wanted.length,
      startMs: (turn && Number.isFinite(turn.startMs)) ? turn.startMs : null,
      endMs: (turn && Number.isFinite(turn.endMs)) ? turn.endMs : null,
    };
  }
  return { kind: 'typed' };
}

/**
 * The structured payload both layers share. PURE. Everything the owner asked
 * for lives in this one object: source kind + span, model identities per stage,
 * the coder's decision trace, the terminology and translate calls, the doctor.
 *
 * @param {object} run
 * @param {string} run.traceId
 * @param {string} run.encounterId
 * @param {{kind:string, text:string}} run.item        what was coded ("diagnosis", "Migraine")
 * @param {{system:string, code:string, display?:string}} run.attached  the coding attached
 * @param {object} run.source                          findVerbatimSpan output
 * @param {string} [run.transcriptRef]                 'Media/<id>' of the transcript, when known
 * @param {object} [run.models]                        { transcription, extraction, draft, provider }
 * @param {object} run.decision                        { rule, candidates, total, query }
 * @param {object} [run.terminology]                   { engine, version }
 * @param {object} [run.translate]                     { input, output }
 * @param {string} run.doctor                          the signing doctor's display name
 * @param {string} [run.doctorRef]                     'Practitioner/<id>' when known
 * @param {string} run.recorded                        ISO instant
 */
export function buildAmbientProvenanceBag(run = {}) {
  const attached = run.attached || {};
  return {
    kind: 'ambient-coding',
    // The per-(encounter, item, coding) idempotency key — the same idea as
    // correctionKey in medplum-consult-extras.js section 4.
    ambientKey: ambientKey(run.encounterId, run.item, attached),
    traceId: run.traceId || null,
    coderVersion: AMBIENT_CODER_VERSION,
    item: { kind: run.item?.kind || 'diagnosis', text: run.item?.text ?? null },
    attached: {
      system: attached.system || null,
      code: attached.code || null,
      display: attached.display || null,
    },
    // WHERE. Typed, or spoken with the quote and its position.
    source: {
      kind: run.source?.kind || 'typed',
      quote: run.source?.quote ?? null,
      speaker: run.source?.speaker ?? null,
      turnIndex: Number.isFinite(run.source?.turnIndex) ? run.source.turnIndex : null,
      charStart: Number.isFinite(run.source?.charStart) ? run.source.charStart : null,
      charEnd: Number.isFinite(run.source?.charEnd) ? run.source.charEnd : null,
      startMs: run.source?.startMs ?? null,
      endMs: run.source?.endMs ?? null,
      transcriptRef: run.transcriptRef || null,
    },
    // THROUGH WHAT. Model identities per stage, read from the server's config.
    models: {
      transcription: run.models?.transcription ?? null,
      extraction: run.models?.extraction ?? null,
      draft: run.models?.draft ?? null,
      provider: run.models?.provider ?? null,
    },
    // DECIDED HOW. The rule that fired and every candidate it weighed.
    decision: {
      rule: run.decision?.rule || null,
      total: Number.isFinite(run.decision?.total) ? run.decision.total : null,
      query: run.decision?.query || null,
      candidates: Array.isArray(run.decision?.candidates)
        ? run.decision.candidates.map((c) => ({ code: c?.code ?? null, display: c?.display ?? null }))
        : [],
    },
    terminology: {
      engine: run.terminology?.engine || 'snowstorm-lite',
      version: run.terminology?.version ?? null,
    },
    translate: run.translate
      ? { input: run.translate.input ?? null, output: run.translate.output ?? null }
      : null,
    signedBy: { display: run.doctor || null, reference: run.doctorRef || null },
    recorded: run.recorded || null,
  };
}

/** The per-(encounter, item, coding) key. One Provenance per attached coding. */
export function ambientKey(encounterId, item, attached) {
  const kind = item?.kind || 'diagnosis';
  const code = `${attached?.system || '?'}|${attached?.code || '?'}`;
  return `${encounterId || 'x'}:${kind}:${code}`;
}

/**
 * The FHIR Provenance resource. PURE — the caller writes it.
 *
 * `target` carries the encounter (so `Provenance?target=Encounter/<id>` finds
 * it, the same search the corrections path uses for idempotency) and the
 * transcript Media when the words were spoken, which is what links the CODE back
 * to the RECORDING it came from.
 */
export function buildAmbientCodingProvenance(run = {}) {
  const bag = buildAmbientProvenanceBag(run);
  const target = [{ reference: `Encounter/${run.encounterId}` }];
  if (run.transcriptRef) target.push({ reference: run.transcriptRef });
  return {
    resourceType: 'Provenance',
    target,
    recorded: run.recorded,
    activity: {
      coding: [{ system: OPD_ACTIVITY_SYSTEM, code: AMBIENT_ACTIVITY_CODE, display: 'ambient-code' }],
    },
    agent: [
      {
        // The doctor whose visit this is. The coding lands on THEIR signed
        // record, so they are the responsible party even though they were never
        // asked — that is exactly why this resource exists.
        type: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type', code: 'author', display: 'Author' }] },
        who: run.doctorRef ? { reference: run.doctorRef, display: run.doctor || undefined } : { display: run.doctor || 'Doctor' },
      },
      {
        // The machine that made the choice, named and versioned.
        type: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/provenance-participant-type', code: 'assembler', display: 'Assembler' }] },
        who: { display: `Healther ambient coder (${AMBIENT_CODER_VERSION})` },
      },
    ],
    ...(run.transcriptRef
      ? { entity: [{ role: 'source', what: { reference: run.transcriptRef } }] }
      : {}),
    extension: [{ url: EXT_OPD_HEALTHER, valueString: JSON.stringify(bag) }],
  };
}

/**
 * The Layer-2 stages for the SAME run — the superset. Each stage repeats the
 * Layer-1 facts in `record` and adds the raw material in `input`/`output`.
 * The caller posts these to `/_pipeline-trace`, which applies the flag, the
 * operator grant and the patient consent before anything is written.
 *
 * @returns {Array<object>} ready to POST as `{ traceId, encounterId, stages }`
 */
export function buildAmbientTraceStages(run = {}) {
  const record = buildAmbientProvenanceBag(run);
  const stages = [
    {
      stage: 'terminology-search',
      seq: 0,
      record,
      model: { id: null, provider: run.terminology?.engine || 'snowstorm-lite' },
      input: run.decision?.query || null,
      output: { total: run.decision?.total ?? null, results: run.decision?.rawResults || run.decision?.candidates || [] },
      durationMs: run.decision?.durationMs ?? null,
      ok: run.decision?.ok !== false,
      error: run.decision?.error || null,
    },
    {
      stage: 'ambient-coder',
      seq: 1,
      record,
      model: { id: AMBIENT_CODER_VERSION, provider: 'deterministic' },
      input: { text: run.item?.text ?? null, source: record.source },
      output: { rule: record.decision.rule, attached: record.attached },
      ok: true,
      error: null,
    },
  ];
  if (run.translate) {
    stages.push({
      stage: 'translate-icd',
      seq: 2,
      record,
      model: { id: run.translate.map || 'snomed-icd10-map', provider: 'deterministic' },
      input: run.translate.input ?? null,
      output: run.translate.output ?? null,
      durationMs: run.translate.durationMs ?? null,
      ok: run.translate.ok !== false,
      error: run.translate.error || null,
    });
  }
  return stages;
}

// ════════════════════════════════════════════════════════════════════════════
// THE WRITE — one Provenance per ambient-attached coding, as the doctor
// ════════════════════════════════════════════════════════════════════════════

/** Decode the opd-healther bag off a resource — {} when it carries none. */
function decodeHealther(resource) {
  if (resource && resource._healther) return resource._healther;
  const e = ((resource && resource.extension) || []).find((x) => x && x.url === EXT_OPD_HEALTHER && x.valueString);
  if (!e) return {};
  try { return JSON.parse(e.valueString) || {}; } catch { return {}; }
}

/**
 * Persist the Provenance for ONE ambient-attached coding.
 *
 * Written AS THE DOCTOR through the shared session, so the per-user AccessPolicy
 * is the only lock (AGENTS §2). The `opd-doctor-rowlevel` policy already grants
 * `Provenance?agent=%profile` create — the same arm the corrections path and the
 * sign Bot use — so no policy change was needed for this feature.
 *
 * IDEMPOTENT. The key is (encounter, item kind, attached coding), stored in the
 * bag as `ambientKey`. A second call for the same coding finds the existing row
 * and writes nothing. This is the same idiom as recordIntakeCorrection
 * (medplum-consult-extras.js section 4), and it matters here because the ambient
 * effect can run again after a reload.
 *
 * NEVER THROWS INTO A CLINICAL SCREEN. The doctor did not ask for this record
 * and must not be interrupted by it, so a write failure returns
 * `{ ok:false, error }` and is printed. This is not a fallback that hides a bug:
 * there is no degraded record to fall back to, the caller gets the failure, and
 * the coding itself is unaffected.
 *
 * @param {object} run  the same shape buildAmbientCodingProvenance takes
 * @returns {Promise<{ok:boolean, provenanceId:string|null, alreadyRecorded?:boolean, error?:string}>}
 */
export async function recordAmbientCodingProvenance(run = {}) {
  if (!run.encounterId) throw new Error('recordAmbientCodingProvenance: encounterId is required');
  if (!run.attached || !run.attached.code) throw new Error('recordAmbientCodingProvenance: attached coding is required');
  const recorded = run.recorded || new Date().toISOString();
  const key = ambientKey(run.encounterId, run.item, run.attached);
  const { medplum } = await getMedplum();

  try {
    // Provenance links its encounter through target[], so the search is by target
    // — the same query recordIntakeCorrection uses for its own idempotency scan.
    const prior = await medplum.searchResources('Provenance', `target=Encounter/${run.encounterId}&_count=200`);
    const existing = (Array.isArray(prior) ? prior : []).find((p) => decodeHealther(p).ambientKey === key);
    if (existing) return { ok: true, provenanceId: existing.id || null, alreadyRecorded: true };

    const prov = await medplum.createResource(buildAmbientCodingProvenance({ ...run, recorded }));
    medplum.invalidateSearches('Provenance');
    return { ok: true, provenanceId: (prov && prov.id) || null, alreadyRecorded: false };
  } catch (e) {
    console.error('[ambient-provenance]', e?.message || e);
    return { ok: false, provenanceId: null, error: e?.message || String(e) };
  }
}

// ════════════════════════════════════════════════════════════════════════════
// THE SIGN STAGE (DEFERRED §102, 2026-08-13)
//
// WHY IT LIVES HERE AND NOT ON THE SERVER. Signing a prescription is a Medplum
// Bot call the browser makes directly (medplum-writes.js signViaBot). There is
// no Express leg in the middle any more — the old POST /prescription/sign was
// deleted with the disk-first engine — and a Bot runs inside Medplum, where it
// cannot write a file on our own server. So the nearest place that sees a sign
// happen is the browser call itself, and that is where this hook sits.
// ════════════════════════════════════════════════════════════════════════════

/**
 * The trace id every SERVER-SIDE stage of one visit uses.
 *
 * THIS MUST STAY THE SAME EXPRESSION AS `flowTraceId` IN
 * server/opd/pipeline-trace.mjs. It is repeated here, not imported, because a
 * browser bundle cannot import a server module. It is one short string join, and
 * the two copies are named the same so a search finds both.
 *
 * The sign stage uses this id — NOT the `amb-…` id — so the signature lands in
 * the same journal group as the transcription, the extraction and the draft of
 * that visit.
 * @param {string} encounterId
 * @returns {string|null}
 */
export function flowTraceId(encounterId) {
  const id = String(encounterId || '').trim();
  return id ? `flow-${id}` : null;
}

/**
 * The Layer-2 stage line for one signature. PURE.
 *
 * `input` is exactly what the sign Bot was given — the encounter, the saved
 * draft's id and version, and the safety override. Nothing clinical is invented
 * here: the Bot re-reads that exact draft version itself, so this is a faithful
 * copy of the request. `output` is the Bot's own answer.
 *
 * @param {object} run
 * @param {string} run.encounterId
 * @param {object} run.input     { draftCompositionId, draftVersionId, overrideSafety }
 * @param {object} [run.result]  the Bot's answer, when it answered
 * @param {number} [run.durationMs]
 * @param {string} [run.error]   the failure text, when it failed
 * @param {string} [run.doctor]  the signing doctor's display name, when known
 * @param {string} [run.doctorRef]
 * @returns {object} one stage, ready for the `/_pipeline-trace` post
 */
export function buildSignTraceStage(run = {}) {
  const result = run.result || null;
  return {
    stage: 'sign',
    // 3 = the last stage of the flow: transcription 0, extraction 1, draft 2.
    seq: 3,
    model: { id: 'sign-visit-bot', provider: 'medplum-bot' },
    durationMs: Number.isFinite(run.durationMs) ? run.durationMs : null,
    ok: !run.error,
    error: run.error || null,
    input: run.input || null,
    output: result,
    record: {
      kind: 'sign',
      signedBy: { display: run.doctor || null, reference: run.doctorRef || null },
      // The three facts an auditor asks about a signature.
      signed: result ? !!result.signed : false,
      blocked: result ? !!result.blocked : false,
      medCount: (result && typeof result.medCount === 'number') ? result.medCount : null,
      draft: run.input || null,
    },
  };
}

/**
 * Post the sign stage to the engineering journal WITHOUT WAITING.
 *
 * THE SIGNATURE MUST NEVER WAIT FOR A LOG, AND MUST NEVER FAIL BECAUSE OF ONE.
 * So this function returns immediately, and every failure inside it — no host
 * runtime registered, no network, the route answering 404 because the Layer-2
 * flag is off (the normal state) — ends here. That is not a fallback hiding a
 * bug: there is no second copy of an engineering log to fall back to, and the
 * clinical record of the signature is the Bot's own transaction, which reports
 * its failures loudly to the caller.
 *
 * @param {object} run the shape `buildSignTraceStage` takes
 * @returns {void}
 */
export function postSignTraceAndForget(run = {}) {
  try {
    if (!run.encounterId) return;
    const body = JSON.stringify({
      traceId: flowTraceId(run.encounterId),
      encounterId: run.encounterId,
      stages: [buildSignTraceStage(run)],
    });
    const url = apiUrl('/api/v1/opd/_pipeline-trace');
    const promise = fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body,
    });
    // Swallow the answer AND any rejection: an unhandled rejection in a browser
    // is a console error the doctor's screen does not need.
    if (promise && typeof promise.then === 'function') promise.then(() => {}, () => {});
  } catch { /* see the contract above — a log may never reach a clinical screen */ }
}

/**
 * Post the Layer-2 stages for this run to the engineering journal.
 *
 * `api` is injected (the consult screen's own helper, which carries the doctor's
 * bearer token) so this module keeps no knowledge of the app's plumbing.
 *
 * A FAILURE IS SILENCE, DELIBERATELY. The route answers 404 while the Layer-2
 * flag is off, which is the normal state, and it answers `written:0` when there
 * is no operator grant or no patient consent. Neither is a clinical event and
 * neither may reach a clinical screen. This is not a fallback that hides a bug:
 * the reason is returned to the caller, and Layer 1 — the record that matters —
 * is written by a separate call that does report its failures.
 *
 * @param {(path: string, init?: object) => Promise<object>} api
 * @param {object} run
 * @returns {Promise<{ok:boolean, written?:number, error?:string}>}
 */
export async function postAmbientTrace(api, run = {}) {
  const stages = buildAmbientTraceStages(run);
  try {
    const j = await api('/_pipeline-trace', {
      method: 'POST',
      body: JSON.stringify({ traceId: run.traceId, encounterId: run.encounterId, stages }),
    });
    return { ok: true, written: (j && j.written) || 0 };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
}
